import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import LocationPicker from '../components/LocationPicker';
import {
  fetchShops,
  createShop,
  updateShop,
  fetchShopImages,
  createShopImage,
  updateShopImage,
  deleteShopImage,
  fetchShopTimings,
  createShopTiming,
  updateShopTiming,
  deleteShopTiming,
  updateShopStatus,
  deleteShop,
} from '../services/shopService';
import { fetchCategories } from '../services/categoryService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash,
  Image as ImageIcon,
  Clock,
} from 'lucide-react';

export default function Shops() {
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    shopCategoryId: '',
    commissionPercentage: '',
    latitude: '',
    longitude: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [selectedShop, setSelectedShop] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [imageForm, setImageForm] = useState({
    file: null,
    preview: '',
    type: 'logo',
    isPrimary: false,
  });
  const [imageLoading, setImageLoading] = useState(false);

  const [timingOpen, setTimingOpen] = useState(false);
  const [timings, setTimings] = useState([]);
  const [timingForm, setTimingForm] = useState({
    dayOfWeek: 'mon',
    opensAt: '08:00',
    closesAt: '21:00',
    isClosed: false,
  });
  const [timingLoading, setTimingLoading] = useState(false);
  const [editingTimingId, setEditingTimingId] = useState(null);

  const defaultTimings = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(
    (d) => ({
      id: d,
      dayOfWeek: d,
      opensAt: '08:00',
      closesAt: '21:00',
      isClosed: false,
    })
  );

  const uniqueTimings = (arr) => {
    const seen = new Set();
    return arr.filter((t) => {
      const key = t.id ?? t.dayOfWeek;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const loadShops = async () => {
    try {
      const res = await fetchShops({
        search,
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter,
        categoryId: categoryFilter,
      });
      const records = res.data.data.records || [];
      const meta = res.data.data.meta || {};
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || 0);
      const shopsWithImages = await Promise.all(
        records.map(async (s) => {
          try {
            const imgRes = await fetchShopImages(s.id);
            const imgs = imgRes.data?.data || [];
            const primary = imgs.find((i) => i.isPrimary) || imgs[0];
            return { ...s, imageUrl: primary ? primary.imageUrl : '' };
          } catch (e) {
            return { ...s, imageUrl: '' };
          }
        })
      );
      setShops(shopsWithImages);
    } catch (err) {
      toast.error('Failed to load shops');
    }
  };

  const loadCategories = async () => {
    try {
      const res = await fetchCategories();
      const records = res.data?.data?.records || [];
      setCategories(records);
    } catch (err) {
      toast.error('Failed to load categories');
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadShops();
  }, [page, limit, sortBy, sortOrder, statusFilter, categoryFilter]);

  useEffect(() => {
    if (search === '') {
      setPage(1);
      loadShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const getCategoryName = (id) => {
    const match = categories.find((c) => String(c.id) === String(id));
    return match ? match.name : id;
  };

  const exportToXls = () => {
    const headers = ['Name', 'Category', 'Phone', 'Email'];
    const rows = shops.map((s) => [s.name, getCategoryName(s.shopCategoryId), s.phone || '', s.email || '']);
    const table = [headers, ...rows]
      .map((r) => r.map((v) => `"${v ?? ''}"`).join('\t'))
      .join('\n');
    const blob = new Blob(['\ufeff', table], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'shops.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name) newErrors.name = 'Name is required';
    if (!form.shopCategoryId) newErrors.shopCategoryId = 'Category is required';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    const payload = {
      ...form,
      shopCategoryId: Number(form.shopCategoryId),
      commissionPercentage: form.commissionPercentage
        ? Number(form.commissionPercentage)
        : undefined,
      latitude: form.latitude ? Number(form.latitude) : undefined,
      longitude: form.longitude ? Number(form.longitude) : undefined,
    };
    try {
      if (editingId) {
        await updateShop(editingId, payload);
        toast.success('Shop updated');
      } else {
        await createShop(payload);
        toast.success('Shop created');
      }
      setForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        shopCategoryId: '',
        commissionPercentage: '',
        latitude: '',
        longitude: '',
      });
      setEditingId(null);
      setOpen(false);
      loadShops();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save shop');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      shopCategoryId: '',
      commissionPercentage: '',
      latitude: '',
      longitude: '',
    });
    setEditingId(null);
    setOpen(true);
    setErrors({});
  };

  const handleEdit = (shop) => {
    setEditingId(shop.id);
    setForm({
      name: shop.name || '',
      email: shop.email || '',
      phone: shop.phone || '',
      address: shop.address || '',
      shopCategoryId: shop.shopCategoryId ? String(shop.shopCategoryId) : '',
      commissionPercentage: shop.commissionPercentage
        ? String(shop.commissionPercentage)
        : '',
      latitude: shop.latitude ? String(shop.latitude) : '',
      longitude: shop.longitude ? String(shop.longitude) : '',
    });
    setOpen(true);
    setErrors({});
  };

  const toggleStatus = async (shop) => {
    const action = shop.status === 'active' ? 'deactivate' : 'activate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this shop?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateShopStatus(
        shop.id,
        shop.status === 'active' ? 'inactive' : 'active'
      );
      toast.success('Status updated');
      loadShops();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this shop?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteShop(id);
      toast.success('Shop deleted');
      loadShops();
    } catch (err) {
      toast.error('Failed to delete shop');
    }
  };

  const openImages = async (shop) => {
    setSelectedShop(shop);
    setImageForm({ file: null, preview: '', type: 'logo', isPrimary: false });
    setImageLoading(true);
    try {
      const res = await fetchShopImages(shop.id);
      setImages(res.data?.data || []);
      setImageOpen(true);
    } catch (err) {
      toast.error('Failed to load images');
    } finally {
      setImageLoading(false);
    }
  };

  const submitImage = async (e) => {
    e.preventDefault();
    if (!selectedShop || !imageForm.file) {
      toast.error('Please select an image');
      return;
    }
    setImageLoading(true);
    try {
      await createShopImage(selectedShop.id, imageForm);
      toast.success('Image added');
      setImageForm({ file: null, preview: '', type: 'logo', isPrimary: false });
      const res = await fetchShopImages(selectedShop.id);
      setImages(res.data?.data || []);
      loadShops();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add image');
    } finally {
      setImageLoading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!selectedShop) return;
    if (!window.confirm('Delete image?')) return;
    setImageLoading(true);
    try {
      await deleteShopImage(selectedShop.id, imageId);
      const res = await fetchShopImages(selectedShop.id);
      setImages(res.data?.data || []);
      loadShops();
    } catch (err) {
      toast.error('Failed to delete image');
    } finally {
      setImageLoading(false);
    }
  };

  const makePrimary = async (imageId) => {
    if (!selectedShop) return;
    setImageLoading(true);
    try {
      await updateShopImage(selectedShop.id, imageId, { isPrimary: true });
      const res = await fetchShopImages(selectedShop.id);
      setImages(res.data?.data || []);
      loadShops();
    } catch (err) {
      toast.error('Failed to update image');
    } finally {
      setImageLoading(false);
    }
  };

  const openTimings = async (shop) => {
    setSelectedShop(shop);
    setTimingForm({
      dayOfWeek: 'mon',
      opensAt: '08:00',
      closesAt: '21:00',
      isClosed: false,
    });
    setEditingTimingId(null);
    setTimingLoading(true);
    try {
      const res = await fetchShopTimings(shop.id);
      const data = res.data?.data || [];
      setTimings(uniqueTimings(data.length ? data : defaultTimings));
      setTimingOpen(true);
    } catch (err) {
      toast.error('Failed to load timings');
    } finally {
      setTimingLoading(false);
    }
  };

  const submitTiming = async (e) => {
    e.preventDefault();
    if (!selectedShop) return;
    setTimingLoading(true);
    try {
      if (editingTimingId && typeof editingTimingId === 'number') {
        await updateShopTiming(selectedShop.id, editingTimingId, timingForm);
        toast.success('Timing updated');
      } else {
        await createShopTiming(selectedShop.id, timingForm);
        toast.success('Timing added');
      }
      setTimingForm({
        dayOfWeek: 'mon',
        opensAt: '08:00',
        closesAt: '21:00',
        isClosed: false,
      });
      setEditingTimingId(null);
      const res = await fetchShopTimings(selectedShop.id);
      const data = res.data?.data || [];
      setTimings(uniqueTimings(data.length ? data : defaultTimings));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save timing');
    } finally {
      setTimingLoading(false);
    }
  };

  const editTiming = (timing) => {
    setEditingTimingId(typeof timing.id === 'number' ? timing.id : null);
    setTimingForm({
      dayOfWeek: timing.dayOfWeek,
      opensAt: timing.opensAt || '08:00',
      closesAt: timing.closesAt || '21:00',
      isClosed: timing.isClosed,
    });
  };

  const deleteTiming = async (timingId) => {
    if (!selectedShop) return;
    if (typeof timingId !== 'number') {
      setTimings(timings.filter((t) => t.id !== timingId));
      return;
    }
    if (!window.confirm('Delete timing?')) return;
    setTimingLoading(true);
    try {
      await deleteShopTiming(selectedShop.id, timingId);
      const res = await fetchShopTimings(selectedShop.id);
      const data = res.data?.data || [];
      setTimings(uniqueTimings(data.length ? data : defaultTimings));
    } catch (err) {
      toast.error('Failed to delete timing');
    } finally {
      setTimingLoading(false);
    }
  };

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <div className="flex space-x-2">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border px-3 py-2 rounded"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2 rounded"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="border px-3 py-2 rounded"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            loadShops();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setPage(1);
                loadShops();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <div className="flex space-x-2">
          <button
            onClick={exportToXls}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export XLS
          </button>
          <button
            onClick={handleAdd}
            className="bg-indigo-600 text-white px-4 py-2 rounded"
          >
            Add Shop
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th className="py-2 px-3">Image</th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Category</th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3">Email</th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => (
              <tr key={shop.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">
                  {shop.imageUrl ? (
                    <img
                      src={shop.imageUrl}
                      alt={shop.name}
                      className="w-12 h-12 object-cover"
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">No image</span>
                  )}
                </td>
                <td className="py-2 px-3">{shop.name}</td>
                <td className="py-2 px-3">{getCategoryName(shop.shopCategoryId)}</td>
                <td className="py-2 px-3">{shop.phone}</td>
                <td className="py-2 px-3">{shop.email}</td>
                <td className="py-2 px-3">{new Date(shop.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${shop.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}
                    onDoubleClick={() => toggleStatus(shop)}
                    title={`Double click to ${shop.status === 'active' ? 'inactive' : 'active'}`}
                  >
                    {shop.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2 flex items-center">
                  <button
                    className="text-indigo-600"
                    onClick={() => handleEdit(shop)}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-indigo-600"
                    onClick={() => openImages(shop)}
                    title="Images"
                  >
                    <ImageIcon size={16} />
                  </button>
                  <button
                    className="text-indigo-600"
                    onClick={() => openTimings(shop)}
                    title="Timings"
                  >
                    <Clock size={16} />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDelete(shop.id)}
                  >
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 space-y-2 md:space-y-0">
        <div className="text-sm text-gray-600">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
        </div>
        <div className="flex justify-center space-x-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            {loading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">
                {editingId ? 'Edit Shop' : 'Add Shop'}
              </h2>
              <button onClick={closeForm} className="text-gray-600">
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
              <div>
                <input
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${errors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <select
                  name="shopCategoryId"
                  value={form.shopCategoryId}
                  onChange={(e) => setForm({ ...form, shopCategoryId: e.target.value })}
                  className={`border px-3 py-2 rounded w-full ${errors.shopCategoryId ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.shopCategoryId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.shopCategoryId}
                  </p>
                )}
              </div>
              <input
                name="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                name="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <textarea
                name="address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Address"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                name="commissionPercentage"
                type="number"
                step="0.01"
                value={form.commissionPercentage}
                onChange={(e) =>
                  setForm({ ...form, commissionPercentage: e.target.value })
                }
                placeholder="Commission Percentage"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <div className="grid grid-cols-1 gap-2">
                <div className="flex space-x-2">
                  <input
                    name="latitude"
                    value={form.latitude}
                    onChange={(e) =>
                      setForm({ ...form, latitude: e.target.value })
                    }
                    placeholder="Latitude"
                    className="border px-3 py-2 rounded w-1/2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    name="longitude"
                    value={form.longitude}
                    onChange={(e) =>
                      setForm({ ...form, longitude: e.target.value })
                    }
                    placeholder="Longitude"
                    className="border px-3 py-2 rounded w-1/2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (pos) =>
                            setForm({
                              ...form,
                              latitude: pos.coords.latitude,
                              longitude: pos.coords.longitude,
                            }),
                          () => toast.error('Failed to get location'),
                          { enableHighAccuracy: true }
                        );
                      } else {
                        toast.error('Geolocation not supported');
                      }
                    }}
                    className="bg-indigo-600 text-white px-3 py-2 rounded w-1/2"
                  >
                    Use Current Location
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMap(true)}
                    className="bg-gray-600 text-white px-3 py-2 rounded w-1/2"
                  >
                    Pick from Map
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-indigo-600 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {loading
                  ? 'Saving...'
                  : editingId
                  ? 'Update Shop'
                  : 'Create Shop'}
              </button>
            </form>
          </div>
        </div>
      )}
      {imageOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full max-w-md p-4 overflow-y-auto rounded">
            {imageLoading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">Images - {selectedShop?.name}</h2>
              <button onClick={() => setImageOpen(false)} className="text-gray-600">
                ×
              </button>
            </div>
            <form onSubmit={submitImage} className="grid gap-2 mb-4">
              <div
                className="border-2 border-dashed rounded p-4 text-center cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    setImageForm({
                      ...imageForm,
                      file,
                      preview: URL.createObjectURL(file),
                    });
                  }
                }}
                onClick={() => document.getElementById('shop-image-upload').click()}
              >
                {imageForm.preview ? (
                  <img
                    src={imageForm.preview}
                    alt=""
                    className="w-full h-48 object-cover mb-2"
                  />
                ) : (
                  <p className="text-gray-500">
                    Drag & drop image or click to browse
                  </p>
                )}
                <input
                  id="shop-image-upload"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setImageForm({
                        ...imageForm,
                        file,
                        preview: URL.createObjectURL(file),
                      });
                    }
                  }}
                />
              </div>
              <select
                value={imageForm.type}
                onChange={(e) => setImageForm({ ...imageForm, type: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="logo">Logo</option>
                <option value="banner">Banner</option>
                <option value="gallery">Gallery</option>
              </select>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={imageForm.isPrimary}
                  onChange={(e) =>
                    setImageForm({ ...imageForm, isPrimary: e.target.checked })
                  }
                />
                <span>Primary</span>
              </label>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
              >
                Add Image
              </button>
            </form>
            <div className="space-y-2">
              {images.map((img) => (
                <div key={img.id} className="flex items-center justify-between">
                  <img
                    src={img.imageUrl}
                    alt=""
                    className="w-16 h-16 object-cover"
                  />
                  <div className="space-x-2">
                    {!img.isPrimary && (
                      <button
                        className="text-green-600 text-sm"
                        onClick={() => makePrimary(img.id)}
                      >
                        Make Primary
                      </button>
                    )}
                    <button
                      className="text-red-600 text-sm"
                      onClick={() => deleteImage(img.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {images.length === 0 && (
                <p className="text-sm text-gray-500">No images</p>
              )}
            </div>
          </div>
        </div>
      )}
      {timingOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full max-w-md p-4 overflow-y-auto rounded">
            {timingLoading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">Timings - {selectedShop?.name}</h2>
              <button onClick={() => setTimingOpen(false)} className="text-gray-600">
                ×
              </button>
            </div>
            <form onSubmit={submitTiming} className="grid gap-2 mb-4">
              <select
                value={timingForm.dayOfWeek}
                onChange={(e) =>
                  setTimingForm({ ...timingForm, dayOfWeek: e.target.value })
                }
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="mon">Monday</option>
                <option value="tue">Tuesday</option>
                <option value="wed">Wednesday</option>
                <option value="thu">Thursday</option>
                <option value="fri">Friday</option>
                <option value="sat">Saturday</option>
                <option value="sun">Sunday</option>
              </select>
              <input
                type="time"
                value={timingForm.opensAt}
                onChange={(e) =>
                  setTimingForm({ ...timingForm, opensAt: e.target.value })
                }
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                type="time"
                value={timingForm.closesAt}
                onChange={(e) =>
                  setTimingForm({ ...timingForm, closesAt: e.target.value })
                }
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={timingForm.isClosed}
                  onChange={(e) =>
                    setTimingForm({ ...timingForm, isClosed: e.target.checked })
                  }
                />
                <span>Closed</span>
              </label>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
              >
                {editingTimingId ? 'Update' : 'Add'} Timing
              </button>
            </form>
            <table className="min-w-full bg-white dark:bg-gray-700">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-600 text-left">
                  <th className="py-1 px-2">Day</th>
                  <th className="py-1 px-2">Opens</th>
                  <th className="py-1 px-2">Closes</th>
                  <th className="py-1 px-2">Closed</th>
                  <th className="py-1 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {timings.map((t) => (
                  <tr key={t.id} className="border-t dark:border-gray-600">
                    <td className="py-1 px-2">{t.dayOfWeek}</td>
                    <td className="py-1 px-2">{t.opensAt}</td>
                    <td className="py-1 px-2">{t.closesAt}</td>
                    <td className="py-1 px-2">{t.isClosed ? 'Yes' : 'No'}</td>
                    <td className="py-1 px-2 space-x-2">
                      <button
                        className="text-indigo-600 text-sm"
                        onClick={() => editTiming(t)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-600 text-sm"
                        onClick={() => deleteTiming(t.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {timings.length === 0 && (
                  <tr>
                    <td className="py-1 px-2" colSpan="5">
                      <p className="text-sm text-gray-500 dark:text-gray-300">No timings</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showMap && (
        <div className="fixed inset-0 bg-black/25 dark:bg-black/50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-4 rounded w-full sm:w-96">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg text-gray-700 dark:text-white">Select Location</h3>
              <button onClick={() => setShowMap(false)} className="text-gray-600 dark:text-gray-300">
                ×
              </button>
            </div>
            <LocationPicker
              lat={form.latitude ? Number(form.latitude) : undefined}
              lng={form.longitude ? Number(form.longitude) : undefined}
              onChange={({ lat, lng }) => {
                setForm({ ...form, latitude: lat, longitude: lng });
                setShowMap(false);
              }}
            />
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
